/**
 * The automated enemies on screen (02 §9.2, ROUND4 §1): mini-bunkers, minitanks and the four
 * vehicles. They live in `world.enemies` like soldiers but are rigid kit models, not rigs, so
 * `Actors` hands them to this class - `isMachine` is the gate, and a kind that falls through it is
 * drawn as a Soldier, which is why it reads the sim's own `isAutomated` rather than a list here.
 * Positions are interpolated and texel-locked exactly as soldiers are, and every hull snaps its
 * heading to the same sixteen directions the men use (05 §6.2), so a lorry turns as they do.
 *
 * What each reads from the sim (enemies/specials): `peeking` is the mini-bunker's LIVE flag
 * (`bunkerLive`) - antenna up and the sensor slot lit while live, both down and dark while dormant;
 * `facing` is where the gun or the nose points; `state === 'dead'` swaps in a wreck over the same
 * spot, which is what the ranger is then stopped by. Which model each kind gets: `machineModels.ts`.
 */

import {
  type BufferGeometry, Group, type Material, Mesh, type Scene, type Texture, Vector3,
} from 'three';
import { bunkerLive, isAutomated } from '../../sim/enemies/specials';
import { Pcg32 } from '../../sim/rng';
import type { Enemy, World } from '../../sim/types';
import type { PixelCamera } from '../camera/PixelCamera';
import { FACING_STEPS } from '../characters/soldier';
import type { KitBuilder } from '../kit/flora';
import { ANTENNA_RAISED_Y, ANTENNA_RETRACTED_Y, miniBunkerAntenna } from '../kit/machines';
import { createEmissiveMaterial } from '../materials/toon';
import type { Palette, TimeOfDay } from '../palette';
import { type MachineModel, machineModel } from './machineModels';
import { glowBoost } from './PropGlow';

/** The antenna travels its 1.4 m in a little under half a second. */
const ANTENNA_SPEED = 3.2;

export const isMachine = (e: Enemy): boolean => isAutomated(e.kind);

interface MachineView {
  root: Group;
  body: Mesh;
  antenna: Mesh | null;
  lamps: Mesh | null;
  model: MachineModel;
  /** Whether the body drawn is the laden one; null until the world has been read once. */
  laden: boolean | null;
  wrecked: boolean;
}

export class Machines {
  private readonly views = new Map<number, MachineView>();
  private readonly geometries = new Map<string, BufferGeometry>();
  private readonly scratch = new Vector3();
  private readonly snapped = new Vector3();
  private readonly lampMaterial: Material;
  private readonly dark: boolean;
  private lastTime = 0;

  constructor(
    private readonly scene: Scene,
    private readonly palette: Palette,
    gradient: Texture,
    /** The enemies' material: machines take the same outer ink outline as the men (05 §8). */
    private readonly material: Material,
    private readonly groundY: (x: number, z: number) => number,
    timeOfDay: TimeOfDay = 'day',
  ) {
    this.lampMaterial = createEmissiveMaterial(gradient, glowBoost(timeOfDay));
    this.dark = timeOfDay === 'night' || timeOfDay === 'dusk';
  }

  /** Every machine of a kind shares one geometry. */
  private geometry(name: string, builder: KitBuilder): BufferGeometry {
    let g = this.geometries.get(name);
    if (!g) {
      g = builder(this.palette, new Pcg32(7, 7)).build();
      this.geometries.set(name, g);
    }
    return g;
  }

  private view(e: Enemy): MachineView {
    let v = this.views.get(e.id);
    if (v) return v;
    const model = machineModel(e.kind);
    const root = new Group();
    const body = new Mesh(this.geometry(`${e.kind}:body`, model.body), this.material);
    body.castShadow = true;
    body.receiveShadow = true;
    root.add(body);
    let antenna: Mesh | null = null;
    let lamps: Mesh | null = null;
    if (e.kind === 'miniBunker') {
      antenna = new Mesh(this.geometry('antenna', miniBunkerAntenna), this.material);
      antenna.castShadow = true;
      // Dormant until the sim says otherwise, so a bunker never flashes "live" on its first frame.
      antenna.position.y = ANTENNA_RETRACTED_Y;
      root.add(antenna);
    }
    if (model.lamps) {
      lamps = new Mesh(this.geometry(`${e.kind}:lamps`, model.lamps), this.lampMaterial);
      lamps.visible = false;
      // Lamps ride on the body, so they turn with the gun or the nose they belong to.
      body.add(lamps);
    }
    this.scene.add(root);
    v = { root, body, antenna, lamps, model, laden: null, wrecked: false };
    this.views.set(e.id, v);
    return v;
  }

  /**
   * A lorry with its canvas laced shut is carrying men; rolled forward over bare hoops it is empty.
   * `Enemy.vehicle.aboard` is the hull's own count - the men are not in `world.enemies` while they
   * ride - so this is the only thing on screen that says which lorry is worth a charge (mission 34).
   */
  private setLoad(e: Enemy, v: MachineView): void {
    const unladen = v.model.unladen;
    if (!unladen) return;
    const laden = (e.vehicle?.aboard ?? 0) > 0;
    if (v.laden === laden) return;
    v.laden = laden;
    v.body.geometry = laden
      ? this.geometry(`${e.kind}:body`, v.model.body)
      : this.geometry(`${e.kind}:unladen`, unladen);
  }

  /** The mini-bunker's slot burns whenever it is live; a vehicle's headlamps only after dark. */
  private lampsOn(e: Enemy): boolean {
    if (e.kind === 'miniBunker') return bunkerLive(e);
    return this.dark;
  }

  update(world: World, time: number, alpha: number, camera: PixelCamera): void {
    const dt = Math.min(0.1, Math.max(0, time - this.lastTime));
    this.lastTime = time;
    const seen = new Set<number>();
    for (const e of world.enemies) {
      if (!isMachine(e)) continue;
      seen.add(e.id);
      const v = this.view(e);
      v.root.visible = true;
      const x = e.prevX + (e.x - e.prevX) * alpha;
      const z = e.prevZ + (e.z - e.prevZ) * alpha;
      camera.snapToTexel(this.scratch.set(x, this.groundY(x, z), z), this.snapped);
      v.root.position.copy(this.snapped);

      if (e.state === 'dead') {
        if (!v.wrecked) {
          v.wrecked = true;
          // The wreck takes the hull's place, so what blocks the road is what you can see blocking it.
          v.body.geometry = this.geometry(`${e.kind}:wreck`, v.model.wreck);
          if (v.antenna) v.antenna.visible = false;
          if (v.lamps) v.lamps.visible = false;
        }
        continue;
      }
      // Heading snaps to the same sixteen directions as the soldiers', for the same reason (05 §6.2).
      const step = (Math.PI * 2) / FACING_STEPS;
      v.body.rotation.y = Math.round(e.facing / step) * step;
      this.setLoad(e, v);
      if (v.lamps) v.lamps.visible = this.lampsOn(e);
      if (v.antenna) {
        const target = bunkerLive(e) ? ANTENNA_RAISED_Y : ANTENNA_RETRACTED_Y;
        const y = v.antenna.position.y;
        v.antenna.position.y = y + Math.max(-ANTENNA_SPEED * dt, Math.min(ANTENNA_SPEED * dt, target - y));
      }
    }
    // Anything the sim has removed - a vehicle that drove off the end of its route - stops being drawn.
    for (const [id, v] of this.views) if (!seen.has(id)) v.root.visible = false;
  }

  dispose(): void {
    for (const v of this.views.values()) this.scene.remove(v.root);
    this.views.clear();
    for (const g of this.geometries.values()) g.dispose();
    this.geometries.clear();
    this.lampMaterial.dispose();
  }
}
